import { apiDataSource, shapeEntity } from 'utils/entities';
import { hasSettled, retryTransient } from 'utils/api/fanOutQueries';
import { DATA_SOURCES } from '@/config/constants';
import { fetchEntities } from 'utils/api/entities.api';
import { useQueries } from 'react-query';

/**
 * Fallback order once the viewed source has been tried. Pop index first: it and
 * TikTok share a taxonomy, and it covers more of it than Google's does.
 */
const FALLBACK_ORDER = [
    DATA_SOURCES.POPULARITY,
    DATA_SOURCES.GOOGLE,
    DATA_SOURCES.TIKTOK,
    DATA_SOURCES.INSTAGRAM,
];

/**
 * One entity's identity from whichever source can answer — the page title, the
 * source tabs, the no-data screen. See frontend/CONTRIBUTING.md, "Pick the hook
 * that names your source".
 */
const useEntityAcrossSources = ({
    id,
    entityType = 'categories',
    dataSource,
    enabled = true,
}) => {
    const order = [
        ...(dataSource ? [dataSource] : []),
        ...FALLBACK_ORDER.filter((source) => source !== dataSource),
    ];

    const results = useQueries(
        order.map((source) => ({
            queryKey: ['entity-across-sources', { id, entityType, source }],
            queryFn: () =>
                fetchEntities({
                    dataSource: apiDataSource(source),
                    entityType,
                    ids: [id],
                }),
            enabled: enabled && !!id,
            staleTime: Infinity,
            retry: retryTransient,
        })),
    );

    // Hold the answer until every source has answered, or the page is titled
    // from whichever one won the race rather than from `order`.
    const isLoading = !results.every(hasSettled);

    const entityBySource = {};
    if (!isLoading)
        results.forEach(({ data }, index) => {
            const entity = data?.[id];
            if (entity) entityBySource[order[index]] = shapeEntity(entity);
        });

    // `order` puts the viewed source first, so the first hit is the best answer.
    const knownSources = order.filter((source) => entityBySource[source]);

    return {
        isLoading,
        entity: knownSources.length ? entityBySource[knownSources[0]] : null,
        // Not just the winner: the tabs ask where this id sits in each source.
        entityBySource,
        knownSources,
    };
};

export default useEntityAcrossSources;
